// components/ExplorationQueue.tsx
import { FC, useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { ExplorationItem } from '../types/exploration';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useSpotifyPlayback } from '../contexts/SpotifyPlaybackContext';
import PlaybackStatusMessage from './PlaybackStatusMessage';
import { compareAlphabetically } from '../utils/sorting';
// Add these imports at the top

interface AutocompleteSuggestion {
  id: string;
  name: string;
  type: 'artist' | 'album';
  image?: string;
  subtitle: string;
}

interface ArtistSuggestion {
  id: string;
  name: string;
  image?: string;
  followers: number;
  genres: string[];
}

type SectionKey = 'backlog' | 'exploring' | 'awesome' | 'decent' | 'meh';

const ExplorationQueue: FC<{ refreshKey?: number }> = ({ refreshKey = 0 }) => {
  const { accessToken } = useAuth();
  const { showToast } = useNotification();
  const { playArtist } = useSpotifyPlayback();
  const router = useRouter();
  const [explorationItems, setExplorationItems] = useState<ExplorationItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [playbackStatuses, setPlaybackStatuses] = useState<{[key: string]: string | null}>({});
  const [editingNotesId, setEditingNotesId] = useState<number | null>(null);
  const [tempNotes, setTempNotes] = useState<{[key: number]: string}>({});
  const [visibleSections, setVisibleSections] = useState<Record<SectionKey, boolean>>({
    backlog: false,
    exploring: true,
    awesome: false,
    decent: false,
    meh: false,
  });

  useEffect(() => {
    fetchExplorationItems();
    if (refreshKey > 0) {
      setVisibleSections(current => ({ ...current, backlog: true }));
    }
  }, [refreshKey]);

  const fetchExplorationItems = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get<{ items: ExplorationItem[] }>('/api/exploration');
      const items = response.data.items || [];
      setExplorationItems(items);
    } catch (error) {
      console.error('Error fetching exploration queue:', error);
      setError('Failed to load exploration queue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (id: number, status: string) => {
    try {
      await axios.put(`/api/exploration/${id}`, { status });
      fetchExplorationItems();
    } catch (error) {
      console.error('Error updating exploration status:', error);
    }
  };

  const handleUpdateRating = async (id: number, rating: number) => {
    try {
      const response = await axios.put<{ artist_name: string }>(`/api/exploration/${id}`, { rating });
      fetchExplorationItems();
      showToast(`${response.data.artist_name} rated ${rating} stars`, 'success');
    } catch (error) {
      console.error('Error updating exploration rating:', error);
      showToast('Failed to update rating', 'error');
    }
  };

  const handlePlayArtist = async (item: ExplorationItem) => {
    if (!item.spotify_artist_id || !accessToken) {
      console.error('Missing spotify_artist_id or access token for artist:', item);
      setPlaybackStatuses(prev => ({
        ...prev,
        [item.id]: "Error: Missing Spotify artist ID or access token"
      }));
      return;
    }

    // Clear previous status
    setPlaybackStatuses(prev => ({ ...prev, [item.id]: null }));

    try {
      const result = await playArtist(item.spotify_artist_id);

      if (result.success) {
        setPlaybackStatuses(prev => ({ ...prev, [item.id]: "Now playing" }));
      } else {
        setPlaybackStatuses(prev => ({ ...prev, [item.id]: `Error: ${result.message}` }));
      }
    } catch (error) {
      console.error('Error playing artist:', error);
      setPlaybackStatuses(prev => ({ ...prev, [item.id]: "Error playing artist" }));
    }

    // Clear status message after 5 seconds
    setTimeout(() => {
      setPlaybackStatuses(prev => ({ ...prev, [item.id]: null }));
    }, 5000);
  };

  const handleShowAlbums = (item: ExplorationItem) => {
    if (!item.spotify_artist_id) {
      console.error('Missing spotify_artist_id for artist:', item);
      return;
    }

    // Navigate to search page with query parameters instead of JSON
    router.push(`/explore?section=find&artistName=${encodeURIComponent(item.artist_name)}&artistId=${item.spotify_artist_id}&view=discography`);
  };

  const [linkingArtist, setLinkingArtist] = useState<number | null>(null);
  const [linkSearchQuery, setLinkSearchQuery] = useState<string>('');
  const [linkSuggestions, setLinkSuggestions] = useState<ArtistSuggestion[]>([]);
  const [showLinkSuggestions, setShowLinkSuggestions] = useState<boolean>(false);

  // Debounced search for linking artists
  const debouncedLinkSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim() || !accessToken) {
        setLinkSuggestions([]);
        return;
      }

      try {
        const response = await axios.get<AutocompleteSuggestion[]>('/api/search/autocomplete', {
          params: { q: query.trim(), access_token: accessToken }
        });
        
        // Filter to only show artists
        const artists = response.data.filter((item) => item.type === 'artist');
        setLinkSuggestions(artists.map((artist) => ({
          id: artist.id,
          name: artist.name,
          image: artist.image,
          followers: parseInt(artist.subtitle.replace(/[^\d]/g, '')) || 0,
          genres: []
        })));
      } catch (error) {
        console.error('Error fetching link suggestions:', error);
        setLinkSuggestions([]);
      }
    }, 300),
    [accessToken]
  );

  useEffect(() => {
    if (linkSearchQuery && linkingArtist) {
      debouncedLinkSearch(linkSearchQuery);
    } else {
      setLinkSuggestions([]);
    }
  }, [linkSearchQuery, linkingArtist, debouncedLinkSearch]);

  const handleStartLinking = (item: ExplorationItem) => {
    setLinkingArtist(item.id);
    setLinkSearchQuery(item.artist_name);
  };

  const handleLinkArtist = async (selectedArtistData: ArtistSuggestion) => {
    if (!linkingArtist) return;

    try {
      await axios.put(`/api/exploration/${linkingArtist}`, {
        spotify_artist_id: selectedArtistData.id,
        artist_image_url: selectedArtistData.image,
        artist_followers: selectedArtistData.followers,
        artist_genres: selectedArtistData.genres
      });

      // Reset linking state
      setLinkingArtist(null);
      setLinkSearchQuery('');
      setLinkSuggestions([]);
      setShowLinkSuggestions(false);

      // Refresh the exploration items
      fetchExplorationItems();
    } catch (error) {
      console.error('Error linking artist:', error);
    }
  };

  const handleCancelLinking = () => {
    setLinkingArtist(null);
    setLinkSearchQuery('');
    setLinkSuggestions([]);
    setShowLinkSuggestions(false);
  };

  // Add these helper functions
  const handleUpdateNotes = async (id: number, notes: string) => {
    try {
      await axios.put(`/api/exploration/${id}`, { notes });
      fetchExplorationItems();
      setEditingNotesId(null);
      // Keep clear tempNotes if needed, but it's probably better to keep it unique per id
      setTempNotes(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (error) {
      console.error('Error updating notes:', error);
      showToast('Failed to update notes', 'error');
    }
  };

  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const itemId = parseInt(draggableId);
    const item = explorationItems.find(i => i.id === itemId);
    if (!item) return;

    let newStatus = item.status;
    let newRating = item.rating;

    switch (destination.droppableId) {
      case 'backlog':
        newStatus = 'Backlog';
        break;
      case 'exploring':
        newStatus = 'Undecided';
        break;
      case 'awesome':
        newStatus = 'Rated';
        newRating = 5;
        break;
      case 'decent':
        newStatus = 'Rated';
        newRating = 3;
        break;
      case 'meh':
        newStatus = 'Rated';
        newRating = 1;
        break;
    }

    // Optimistic update
    const updatedItems = explorationItems.map(i => {
      if (i.id === itemId) {
        return { ...i, status: newStatus, rating: newRating };
      }
      return i;
    });
    setExplorationItems(updatedItems);

    try {
      await axios.put(`/api/exploration/${itemId}`, { 
        status: newStatus, 
        rating: newRating 
      });
      fetchExplorationItems();
      
      if (newStatus === 'Rated') {
        showToast(`${item.artist_name} moved to ${destination.droppableId} with ${newRating} stars`, 'success');
      } else {
        showToast(`${item.artist_name} moved to ${destination.droppableId}`, 'success');
      }
    } catch (error) {
      console.error('Error updating item on drag end:', error);
      showToast('Failed to update item position', 'error');
      fetchExplorationItems(); // Revert on error
    }
  };

  const groupedItems = {
    backlog: (explorationItems || [])
      .filter(item => item.status === 'Backlog')
      .sort((a, b) => compareAlphabetically(a.artist_name, b.artist_name)),
    exploring: (explorationItems || [])
      .filter(item => item.status === 'Undecided')
      .sort((a, b) => compareAlphabetically(a.artist_name, b.artist_name)),
    awesome: (explorationItems || [])
      .filter(item => item.status === 'Rated' && (item.rating || 0) >= 4)
      .sort((a, b) => compareAlphabetically(a.artist_name, b.artist_name)),
    decent: (explorationItems || [])
      .filter(item => item.status === 'Rated' && (item.rating || 0) === 3)
      .sort((a, b) => compareAlphabetically(a.artist_name, b.artist_name)),
    meh: (explorationItems || [])
      .filter(item => item.status === 'Rated' && (item.rating || 0) <= 2 && (item.rating || 0) > 0)
      .sort((a, b) => compareAlphabetically(a.artist_name, b.artist_name))
  };

  const renderCard = (item: ExplorationItem, index: number) => (
    <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`mb-1 px-2 py-1.5 bg-white border-b border-gray-200 ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-green-500 ring-opacity-50' : ''
          }`}
        >
          <div className="flex items-center mb-1">
            <div className="flex-grow overflow-hidden">
              <p className="font-bold truncate" title={item.artist_name}>{item.artist_name}</p>
              {item.artist_followers && (
                <p className="text-xs text-gray-500">
                  {item.artist_followers.toLocaleString()} followers
                </p>
              )}
            </div>
          </div>

          {editingNotesId === item.id ? (
            <div className="mb-1">
              <textarea
                value={tempNotes[item.id] !== undefined ? tempNotes[item.id] : (item.notes || '')}
                onChange={(e) => setTempNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                className="w-full p-2 text-sm border rounded mb-2 focus:ring-1 focus:ring-green-500 outline-none"
                rows={2}
                placeholder="Add a comment..."
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setEditingNotesId(null);
                    setTempNotes(prev => ({ ...prev, [item.id]: '' }));
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateNotes(item.id, tempNotes[item.id] || '')}
                  className="text-sm bg-gray-700 text-white px-2 py-1 rounded hover:bg-gray-600"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div 
              className="mb-1 px-1 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 group relative truncate"
              onClick={() => {
                setEditingNotesId(item.id);
                setTempNotes(prev => ({ ...prev, [item.id]: item.notes || '' }));
              }}
            >
              {item.notes ? `"${item.notes}"` : <span className="text-gray-400">Add comment...</span>}
              <span className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-sm text-gray-400">
                ✎
              </span>
            </div>
          )}

          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {item.tags.map(tag => (
                <span
                  key={tag.id}
                  className="bg-blue-50 text-blue-700 text-xs px-1.5 py-0.5 rounded-full border border-blue-100"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}

          {item.status === 'Rated' && (
            <div className="flex text-yellow-500 mb-1 text-sm">
              {Array.from({ length: item.rating || 0 }).map((_, i) => (
                <span key={i}>★</span>
              ))}
              {Array.from({ length: 5 - (item.rating || 0) }).map((_, i) => (
                <span key={i} className="text-gray-300">★</span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-1 mt-1">
            {item.status === 'Backlog' && (
              <button
                onClick={() => handleUpdateStatus(item.id, 'Undecided')}
                className="bg-blue-700 hover:bg-blue-600 text-white text-sm px-2 py-1 rounded transition-colors"
              >
                Explore
              </button>
            )}

            {(item.status === 'Undecided') && (
              <div className="flex items-center gap-1 w-full mb-2">
                <span className="text-sm text-gray-500 mr-1">Rate:</span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => handleUpdateRating(item.id, star)}
                      className="text-lg focus:outline-none hover:scale-120 transition-transform px-0.5"
                    >
                      {star <= (item.rating || 0) ? '★' : '☆'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {item.spotify_artist_id ? (
              <>
                <button
                  onClick={() => handlePlayArtist(item)}
                  className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-2 py-1 rounded transition-colors"
                >
                  Play
                </button>
                <button
                  onClick={() => handleShowAlbums(item)}
                  className="bg-purple-700 hover:bg-purple-600 text-white text-sm px-2 py-1 rounded transition-colors"
                >
                  Albums
                </button>
              </>
            ) : (
              <button
                onClick={() => handleStartLinking(item)}
                className="bg-orange-700 hover:bg-orange-600 text-white text-sm px-2 py-1 rounded transition-colors"
              >
                Link Spotify
              </button>
            )}

          </div>

          <PlaybackStatusMessage status={playbackStatuses[item.id]} />
          
          {/* Inline Spotify Linking Interface */}
          {linkingArtist === item.id && (
            <div className="mt-2 p-2 bg-white rounded border border-orange-200 shadow-inner">
              <input
                type="text"
                placeholder="Search Spotify..."
                value={linkSearchQuery}
                onChange={(e) => setLinkSearchQuery(e.target.value)}
                onFocus={() => setShowLinkSuggestions(true)}
                className="w-full p-2 border rounded text-sm mb-2"
                autoFocus
              />
              {showLinkSuggestions && linkSuggestions.length > 0 && (
                <div className="max-h-40 overflow-y-auto mb-2 border rounded">
                  {linkSuggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      onClick={() => handleLinkArtist(suggestion)}
                      className="flex items-center p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="overflow-hidden">
                        <div className="font-semibold text-sm truncate">{suggestion.name}</div>
                        <div className="text-sm text-gray-500">{suggestion.followers.toLocaleString()} followers</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={handleCancelLinking}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Kanban Board */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex flex-col lg:flex-row gap-3 lg:overflow-x-auto pb-3">
          {/* Backlog Column */}
          {visibleSections.backlog && <Droppable droppableId="backlog">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="w-full lg:flex-shrink-0 lg:w-80 flex flex-col"
              >
                <div className="flex items-center justify-between mb-4 px-2">
                  <h3 className="font-bold text-gray-700 uppercase text-sm tracking-wider">Backlog</h3>
                  <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-sm font-bold">
                    {groupedItems.backlog.length}
                  </span>
                </div>
                <div className="bg-gray-100 p-1 rounded-lg min-h-16 flex-grow">
                  {groupedItems.backlog.map((item, index) => renderCard(item, index))}
                  {groupedItems.backlog.length === 0 && (
                    <p className="text-gray-400 text-center py-10 text-sm italic">Empty</p>
                  )}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>}

          {/* Exploring Column */}
          {visibleSections.exploring && <Droppable droppableId="exploring">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="w-full lg:flex-shrink-0 lg:w-80 flex flex-col"
              >
                <div className="flex items-center justify-between mb-4 px-2">
                  <h3 className="font-bold text-blue-600 uppercase text-sm tracking-wider">Exploring</h3>
                  <span className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-sm font-bold">
                    {groupedItems.exploring.length}
                  </span>
                </div>
                <div className="bg-blue-50 p-1 rounded-lg min-h-16 flex-grow">
                  {groupedItems.exploring.map((item, index) => renderCard(item, index))}
                  {groupedItems.exploring.length === 0 && (
                    <p className="text-blue-300 text-center py-10 text-sm italic">Empty</p>
                  )}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>}

          {/* Awesome Column */}
          {visibleSections.awesome && <Droppable droppableId="awesome">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="w-full lg:flex-shrink-0 lg:w-80 flex flex-col"
              >
                <div className="flex items-center justify-between mb-4 px-2">
                  <h3 className="font-bold text-green-600 uppercase text-sm tracking-wider">Awesome</h3>
                  <span className="bg-green-100 text-green-600 px-2 py-0.5 rounded-full text-sm font-bold">
                    {groupedItems.awesome.length}
                  </span>
                </div>
                <div className="bg-green-50 p-1 rounded-lg min-h-16 flex-grow border border-green-100">
                  {groupedItems.awesome.map((item, index) => renderCard(item, index))}
                  {groupedItems.awesome.length === 0 && (
                    <p className="text-green-300 text-center py-10 text-sm italic">Empty</p>
                  )}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>}

          {/* Decent Column */}
          {visibleSections.decent && <Droppable droppableId="decent">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="w-full lg:flex-shrink-0 lg:w-80 flex flex-col"
              >
                <div className="flex items-center justify-between mb-4 px-2">
                  <h3 className="font-bold text-yellow-600 uppercase text-sm tracking-wider">Decent</h3>
                  <span className="bg-yellow-100 text-yellow-600 px-2 py-0.5 rounded-full text-sm font-bold">
                    {groupedItems.decent.length}
                  </span>
                </div>
                <div className="bg-yellow-50 p-1 rounded-lg min-h-16 flex-grow border border-yellow-100">
                  {groupedItems.decent.map((item, index) => renderCard(item, index))}
                  {groupedItems.decent.length === 0 && (
                    <p className="text-yellow-300 text-center py-10 text-sm italic">Empty</p>
                  )}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>}

          {/* Meh Column */}
          {visibleSections.meh && <Droppable droppableId="meh">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="w-full lg:flex-shrink-0 lg:w-80 flex flex-col"
              >
                <div className="flex items-center justify-between mb-4 px-2">
                  <h3 className="font-bold text-red-600 uppercase text-sm tracking-wider">Meh</h3>
                  <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-sm font-bold">
                    {groupedItems.meh.length}
                  </span>
                </div>
                <div className="bg-red-50 p-1 rounded-lg min-h-16 flex-grow border border-red-100">
                  {groupedItems.meh.map((item, index) => renderCard(item, index))}
                  {groupedItems.meh.length === 0 && (
                    <p className="text-red-300 text-center py-10 text-sm italic">Empty</p>
                  )}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>}
        </div>
      </DragDropContext>

      <div className="compact-panel mb-3 flex flex-wrap gap-2" aria-label="Visible Explore sections">
        {(Object.keys(groupedItems) as SectionKey[]).map(section => (
            <button
                key={section}
                type="button"
                aria-pressed={visibleSections[section]}
                onClick={() => setVisibleSections(current => ({ ...current, [section]: !current[section] }))}
                className={`compact-button rounded px-3 text-sm font-semibold ${visibleSections[section] ? 'bg-gray-600 text-white' : 'bg-gray-800 text-gray-400'}`}
            >
              {section === 'backlog' ? 'Backlog' : section.charAt(0).toUpperCase() + section.slice(1)} ({groupedItems[section].length})
            </button>
        ))}
      </div>
    </div>
  );
};

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default ExplorationQueue;
